export interface LeadStatus {
    id: string;
    name: string;
    label: string;
    color: string;
}

// Lead interface - represents potential customers who haven't been converted yet
// Leads cannot login, trade, or have KYC status until converted to customers
export interface Lead {
    id: string;
    customer_number?: string;
    name: string;
    email: string;
    phone?: string;
    additional_phone_number?: string;
    country?: string;
    preferred_language?: string;
    date_of_birth?: string;
    status: LeadStatus;
    notes?: any[];
    tasks?: any[];
    activities?: any[];
    assigned_user_id?: string;
    assigned_user?: {
        id: string;
        name: string;
        email: string;
        title?: string | null;
    };
    contact_attempts?: number;
    last_contact_date?: string | null;
    next_follow_up_date?: string | null;
    custom_attributes?: any[];
    settings?: Record<string, any> | null;
    created_at: string;
    updated_at: string;
    converted_at?: string;

    /**
     * Campaigns module (quantix-api/docs/campaigns/TASKS.md, task T5): set when the lead
     * was created from a campaign submission. `campaign` is the resolved `{id,name}` the
     * API is expected to add alongside `campaign_id` — until then it is read defensively
     * (see src/pages/crm/Leads/LeadDetailsPage.tsx).
     */
    campaign_id?: string | null;
    campaign?: { id: string; name: string } | null;

    // Legacy fields for backward compatibility
    lead_source?: string;
    lead_source_label?: string;
    marketing_campaign_id?: string;
    sales_team?: string;
    ib_referral_id?: string;
    preferred_contact_method?: string;
    preferred_contact_method_label?: string;
    instruments_of_interest?: string[];
    time_zone?: string;
    regulatory_region?: string;
    behavior?: Record<string, any>;
    custom_fields?: Record<string, any>;
    created_by?: string;
    updated_by?: string;
    creator?: {
        id: string;
        name: string;
        email: string;
    };
    updater?: {
        id: string;
        name: string;
        email: string;
    };
}

export interface CreateLeadRequest {
    name: string;
    email: string;
    phone?: string;
    additional_phone_number?: string;
    country?: string;
    preferred_language?: string;
    date_of_birth?: string;
    status?: string;
    lead_status?: string;
    lead_source?: string;
    marketing_campaign_id?: string;
    sales_team?: string;
    ib_referral_id?: string;
    contact_attempts?: number;
    last_contact_date?: string;
    next_follow_up_date?: string;
    preferred_contact_method?: string;
    instruments_of_interest?: string[];
    time_zone?: string;
    regulatory_region?: string;
    behavior?: Record<string, any>;
    custom_fields?: Record<string, any>;
    custom_attributes?: Record<string, any>;
    notes?: string;
    assigned_user_id?: string;
}

export interface UpdateLeadRequest extends Partial<CreateLeadRequest> {}

// Contact Method Options
export const CONTACT_METHOD_OPTIONS = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'telegram', label: 'Telegram' },
    { value: 'skype', label: 'Skype' },
    { value: 'zoom', label: 'Zoom' },
    { value: 'teams', label: 'Microsoft Teams' },
] as const;

// Instrument Options
export const INSTRUMENT_OPTIONS = [
    { value: 'forex', label: 'Forex' },
    { value: 'crypto', label: 'Cryptocurrency' },
    { value: 'stocks', label: 'Stocks' },
    { value: 'commodities', label: 'Commodities' },
    { value: 'indices', label: 'Indices' },
    { value: 'bonds', label: 'Bonds' },
    { value: 'etfs', label: 'ETFs' },
] as const;
